import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import HeaderComp from "./Components/HeaderComp";
import Footer from "./Components/Footer";
import Blackboard from "./Components/Blackboard";
import { useLang } from "../i18n/LanguageContext";
import { collection, getDocs } from "firebase/firestore";
import { getPublicTeachers } from "../utils/publicProfile";
import { db } from "../firebase";
import thomasImg from "../media/thomas-sm.jpg";
import sylvieImg from "../media/sylvie-sm.jpg";
import dalilImg from "../media/dalil-sm.jpg";
import afremImg from "../media/afrem-sm.jpg";
import {
  PiMathOperationsDuotone,
  PiGlobeHemisphereWestDuotone,
  PiTranslateDuotone,
  PiFlaskDuotone,
  PiScalesDuotone,
  PiCodeDuotone,
  PiMusicNotesDuotone,
  PiPaletteDuotone,
  PiGraduationCapDuotone,
  PiChalkboardTeacherDuotone,
  PiMagnifyingGlassDuotone,
  PiUsersThreeDuotone,
  PiSparkleDuotone,
  PiArrowRightBold,
  PiQuotesFill,
} from "react-icons/pi";

// Fonds (comme la sauvegarde) : blanc #ffffff, crème #f9f5f0, prune #502887, or #f5a926
// Une seule famille d'icônes (Phosphor, style duotone) sur toute la page
const CATEGORIES = [
  { Icon: PiMathOperationsDuotone, label: "Mathématiques" },
  { Icon: PiGlobeHemisphereWestDuotone, label: "Géographie" },
  { Icon: PiTranslateDuotone, label: "Langues" },
  { Icon: PiFlaskDuotone, label: "Sciences" },
  { Icon: PiScalesDuotone, label: "Droit" },
  { Icon: PiCodeDuotone, label: "Informatique" },
  { Icon: PiMusicNotesDuotone, label: "Musique" },
  { Icon: PiPaletteDuotone, label: "Arts" },
];

const STEPS = [
  { Icon: PiMagnifyingGlassDuotone, title: "Trouve un cours", desc: "Parcours les cours disponibles ou contacte un professeur pour en créer un sur mesure." },
  { Icon: PiUsersThreeDuotone, title: "Inscris-toi", desc: "Rejoins la classe. Le prix est partagé entre tous les élèves inscrits — plus on est, moins c'est cher." },
  { Icon: PiSparkleDuotone, title: "Apprends ensemble", desc: "Participe au cours en ligne ou en présentiel, interagis avec ton professeur et tes camarades." },
];

const TESTIMONIALS = [
  { name: "Thomas", image: thomasImg, quote: "Grâce à Tatie Soso, j'ai rattrapé mes lacunes en maths en seulement 3 séances. Je recommande !" },
  { name: "Sylvie", image: sylvieImg, quote: "Une plateforme intuitive et des professeurs bienveillants. Mes enfants adorent !" },
  { name: "Dalil", image: dalilImg, quote: "Le concept de partager le coût entre élèves est vraiment innovant. Bravo !" },
  { name: "Afrem", image: afremImg, quote: "J'ai trouvé un cours de droit en 24h. Tatie Soso c'est magique !" },
];

// Titre de section : petite accroche manuscrite + titre Alegreya, identiques partout
const SectionHeader = ({ eyebrow, title, subtitle, dark = false }) => (
  <div className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
    {eyebrow && (
      <p className={`font-chalk text-2xl mb-1 ${dark ? "text-or" : "text-prune"}`}>{eyebrow}</p>
    )}
    <h2 className={`font-display font-bold text-3xl md:text-4xl leading-tight ${dark ? "text-white" : "text-prune-deep"}`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`mt-3 text-base md:text-lg ${dark ? "text-white/85" : "text-stone-700"}`}>{subtitle}</p>
    )}
  </div>
);

const AudienceCard = ({ Icon, title, text, to, cta, variant }) => {
  const isStudent = variant === "student";
  return (
    <article className="relative flex flex-col bg-white rounded-3xl p-7 md:p-9 shadow-carte border border-lin overflow-hidden">
      <span className={`absolute inset-x-0 top-0 h-1.5 ${isStudent ? "bg-prune" : "bg-or"}`} aria-hidden="true" />
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${
          isStudent ? "bg-prune-light text-prune" : "bg-or-light text-prune-deep"
        }`}
      >
        <Icon size={30} aria-hidden="true" />
      </div>
      <h3 className="font-display font-bold text-2xl text-prune-deep mb-3">{title}</h3>
      <p className="text-stone-600 leading-relaxed mb-7 flex-1">{text}</p>
      <Link
        to={to}
        className={`self-start inline-flex items-center gap-2 min-h-[44px] px-6 rounded-full font-bold transition-colors ${
          isStudent
            ? "bg-prune text-white hover:bg-prune-dark"
            : "bg-or text-prune-deep hover:bg-or-dark"
        }`}
      >
        {cta}
        <PiArrowRightBold aria-hidden="true" />
      </Link>
    </article>
  );
};

const TeacherCard = ({ teacher }) => {
  const { t } = useLang();
  const name = [teacher.firstName, teacher.lastName].filter(Boolean).join(" ") || teacher.displayName;
  return (
    <Link
      to={`/Teacher/${teacher.id}`}
      className="group flex flex-col items-center text-center bg-white rounded-3xl p-6 border border-lin shadow-carte hover:shadow-carte-hover transition-shadow duration-200"
    >
      <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-prune-light mb-4">
        {teacher.profilePicture ? (
          <img src={teacher.profilePicture} alt="" width="96" height="96" loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-prune flex items-center justify-center text-white text-3xl font-bold" aria-hidden="true">
            {teacher.firstName ? teacher.firstName[0] : "?"}
          </div>
        )}
      </div>
      <h3 className="font-display font-bold text-lg text-prune-deep">{name}</h3>
      <p className="text-sm text-stone-700 mt-1 line-clamp-2 flex-1">{teacher.description || t("Professeur Tatie Soso")}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-prune group-hover:gap-2.5 transition-all">
        {t("Voir le profil")}
        <PiArrowRightBold aria-hidden="true" />
      </span>
    </Link>
  );
};

function Home() {
  const [teachers, setTeachers] = useState([]);
  const [slides, setSlides] = useState([]);
  const { t } = useLang();

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        setTeachers(await getPublicTeachers());
      } catch (e) {
        console.error("Erreur chargement professeurs:", e);
      }
    };

    const fetchSlides = async () => {
      try {
        const snapshot = await getDocs(collection(db, "slides"));
        const slideList = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((s) => s.text);
        setSlides(slideList);
      } catch (e) {
        console.error("Erreur chargement slides:", e);
      }
    };

    fetchTeachers();
    fetchSlides();
  }, []);


  return (
    <div className="min-h-screen bg-white">
      <HeaderComp />

      <main>
        {/* ─── Hero : tableau noir ─── */}
        <Blackboard slides={slides} />

        {/* ─── Étudiant / Professeur ─── */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20">
          <SectionHeader eyebrow={t("Bienvenue !")} title={t("Tatie Soso, c'est pour qui ?")} />
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <AudienceCard
              variant="student"
              Icon={PiGraduationCapDuotone}
              title={t("Je suis étudiant·e")}
              text={t("Parce que l'éducation ne se limite pas à l'amélioration des résultats scolaires, Tatie Soso a pour vocation d'éveiller la curiosité des jeunes esprits. Trouve un cours adapté à tes besoins, rejoins une classe et apprends à ton rythme.")}
              to="/cours_a_venir"
              cta={t("Voir les cours")}
            />
            <AudienceCard
              variant="teacher"
              Icon={PiChalkboardTeacherDuotone}
              title={t("Je suis professeur·e")}
              text={t("Il n'est pas nécessaire d'être maître d'école pour donner des cours particuliers. Tatie Soso encourage tout un chacun à offrir de son temps et de sa personne pour un partage unique de connaissances. Les seuls maîtres mots sont bienveillance et apprentissage.")}
              to="/professeurs"
              cta={t("Rejoindre l'équipe")}
            />
          </div>
        </section>

        {/* ─── Matières ─── */}
        <section className="bg-creme py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionHeader
              eyebrow={t("Un peu de tout")}
              title={t("Les matières")}
              subtitle={t("Trouve la matière qui te fait envie et découvre les prochains cours.")}
            />
            <ul className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
              {CATEGORIES.map(({ Icon, label }) => (
                <li key={label}>
                  <Link
                    to="/cours_a_venir"
                    className="group h-full flex flex-col items-center gap-3 rounded-2xl bg-white border border-lin p-5 md:p-6 text-center hover:border-prune/30 hover:shadow-carte-hover transition-shadow duration-200"
                  >
                    <span className="w-14 h-14 rounded-2xl bg-prune-light text-prune flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
                      <Icon size={30} aria-hidden="true" />
                    </span>
                    <span className="font-bold text-prune-deep">{t(label)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ─── Professeurs ─── */}
        {teachers.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20">
            <SectionHeader
              eyebrow={t("L'équipe")}
              title={t("Nos professeurs")}
              subtitle={t("Des passionné·es prêt·es à partager leur savoir")}
            />
            {/* Cartes centrées, même s'il n'y a qu'un ou deux profs */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,260px))] justify-center gap-5 md:gap-6">
              {teachers.slice(0, 4).map((tc) => (
                <TeacherCard key={tc.id} teacher={tc} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/professeurs"
                className="inline-flex items-center gap-2 min-h-[44px] px-7 rounded-full font-bold text-prune border-2 border-prune hover:bg-prune hover:text-white transition-colors"
              >
                {t("Voir tous les professeurs")}
                <PiArrowRightBold aria-hidden="true" />
              </Link>
            </div>
          </section>
        )}

        {/* ─── Comment ça marche ─── */}
        <section className="on-dark bg-prune text-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionHeader dark eyebrow={t("Simple comme bonjour")} title={t("Comment ça marche ?")} />
            <ol className="relative grid md:grid-cols-3 gap-10 md:gap-8">
              {/* Fil pointillé entre les étapes (grand écran) */}
              <span
                className="hidden md:block absolute top-9 left-[16.66%] right-[16.66%] border-t-2 border-dashed border-white/25"
                aria-hidden="true"
              />
              {STEPS.map(({ Icon, title, desc }, i) => (
                <li key={title} className="relative flex flex-col items-center text-center">
                  <div className="relative w-[72px] h-[72px] rounded-full bg-prune-dark border border-white/20 flex items-center justify-center mb-5">
                    <Icon size={34} className="text-or" aria-hidden="true" />
                    <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-or text-prune-deep text-sm font-extrabold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2">{t(title)}</h3>
                  <p className="text-white/85 leading-relaxed max-w-xs">{t(desc)}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ─── Avis ─── */}
        <section className="bg-or py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionHeader eyebrow={t("Ils en parlent")} title={t("Ils nous font confiance")} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {TESTIMONIALS.map(({ name, image, quote }) => (
                <figure key={name} className="flex flex-col bg-white rounded-3xl p-6 shadow-carte">
                  <PiQuotesFill size={30} className="text-or-dark mb-3" aria-hidden="true" />
                  <blockquote className="text-stone-700 leading-relaxed flex-1">{t(quote)}</blockquote>
                  <figcaption className="flex items-center gap-3 mt-5 pt-5 border-t border-lin">
                    <img
                      src={image}
                      alt=""
                      width="48"
                      height="48"
                      loading="lazy"
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-or"
                    />
                    <span className="font-bold text-prune-deep">{name}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer flush />
    </div>
  );
}

export default Home;
